import * as React from "react";
import { Button, Divider, Grid, Tab, Segment } from "semantic-ui-react";

import { useSelector } from "react-redux";

import { Detail } from "@neworbit/simpleui-detail";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import {
    PHYSIOTHERAPY_ORIGINS,
    CaseType,
    COVID_19_FEATURES,
    PRODUCT_TYPES,
    ASSESSMENT_AND_WELL_BEING_FEATURES,
    getYesNoOptionEnabledDisplayValue
} from "@teamprevent/common/model";

import { CoreDetailProps, DetailProps } from "../Detail";
import DeleteButton from "../DeleteButton";
import { ADMIN_ROLES, Authorize } from "../../../../auth";
import { Customer, accessGroupOptions } from "../../model";
import { customersSelector } from "../../selectors";

import { CoreDetailsButtons } from "./CoreDetailButtons";

import { Link } from "redux-little-router";

export const CoreDetails: React.FC<CoreDetailProps> = (props) => {
    const customers: Customer[] = useSelector(customersSelector);
    const ppqAutomationFeatureEnabled = getFeatureToggles()[FeatureToggleNames.EnablePPQAutomation];
    const hsCaseAutomationForNightWorker = getFeatureToggles()[FeatureToggleNames.HSCaseAutomationForNightWorker];
    const customerLevelPartialMatchChanges = getFeatureToggles()[FeatureToggleNames.CustomerLevelPartialMatchChanges];
    const enableSNCAndFinanceInfoFunctionality = getFeatureToggles()[FeatureToggleNames.EnableSNCAndFinanceInfoFunctionality];

    const getAccessGroupDisplayText = (accessGroup: string | undefined) => {
        return accessGroupOptions.find(x => x.value === accessGroup)?.text ?? "";
    };

    const getDisplayValue = (value: string | undefined | null): string => {
        return value ?? "";
    };

    return (
        <Grid>
            <Divider hidden={true} />
            <h3>CUSTOMER INFORMATION</h3>
            <Detail label="SSO" value={props.customer?.ssoCustomer ? "Yes" : "No"} labelWidth={5} valueWidth={11} />
            <Detail label="OH Direct Customer" value={props.customer?.ohDirectCustomer ? "Yes" : "No"} labelWidth={5} valueWidth={11} />
            <Detail label="Access Groups" value={getAccessGroupDisplayText(props.customer.accessGroupMode)} labelWidth={5} valueWidth={11} />
            <Detail label="Subscribed" value={props.customer?.subscribed ? "Yes" : "No"} labelWidth={5} valueWidth={11} />
            <Detail label="Name" value={getDisplayValue(props.customer.name)} labelWidth={5} valueWidth={11} />
            <Detail label="Customer Category" value={getDisplayValue(props.customer.customerCategory?.name)} labelWidth={5} valueWidth={11} />
            <Detail label="Prefix" value={getDisplayValue(props.customer.prefix)} labelWidth={5} valueWidth={11} />
            <Detail label="Email Address" value={getDisplayValue(props.customer.emailAddress)} labelWidth={5} valueWidth={11} />
            <Detail label="Phone Number" value={getDisplayValue(props.customer.phoneNumber)} labelWidth={5} valueWidth={11} />
            <Detail label="Address Line 1" value={getDisplayValue(props.customer.addressLine1)} labelWidth={5} valueWidth={11} />
            <Detail label="Address Line 2" value={getDisplayValue(props.customer.addressLine2)} labelWidth={5} valueWidth={11} />
            <Detail label="Address Line 3" value={getDisplayValue(props.customer.addressLine3)} labelWidth={5} valueWidth={11} />
            <Detail label="City" value={getDisplayValue(props.customer.city)} labelWidth={5} valueWidth={11} />
            <Detail label="County" value={getDisplayValue(props.customer.county)} labelWidth={5} valueWidth={11} />
            <Detail label="PostCode" value={getDisplayValue(props.customer.postCode)} labelWidth={5} valueWidth={11} />
            <Detail label="Sectors" value={getDisplayValue(props.customer?.sectors?.join(", "))} labelWidth={5} valueWidth={11} />
            <Detail label="Domain Name" value={getDisplayValue(props.customer.domainName)} labelWidth={5} valueWidth={11} />
            <Detail label="Allowed Email Domains" value={getDisplayValue(props.customer?.allowedEmailDomains?.join(", "))} labelWidth={5} valueWidth={11} />
            <Detail label="Key Contacts" value={getDisplayValue(props.customer.keyContacts)} labelWidth={5} valueWidth={11} />
            <Detail
                label="Partners"
                value={props.customer.partners && props.customer.partners.length ?
                    props.customer.partners.map(p => customers.find(x => x.id === p.id)?.name)?.join(", ") :
                    "None"}
                labelWidth={5}
                valueWidth={11}
            />
            <Detail
                label="Products"
                value={props.customer.products && props.customer.products.length ?
                    props.customer.products.map(p => PRODUCT_TYPES.find(x => x.value === p)?.displayName)?.join(", ") :
                    "None"}
                labelWidth={5}
                valueWidth={11}
            />
            { hsCaseAutomationForNightWorker && props.customer.products && props.customer.products.includes(CaseType.HEALTH_SURVEILLANCE) &&
                <Detail
                    label="Automated HS"
                    value={getYesNoOptionEnabledDisplayValue(props.customer.hsNightWorkerAutomationEnabled)}
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            { props.customer?.products?.includes(CaseType.GENERIC) &&
                <Detail
                    label="Generic Case Types"
                    value={props.customer?.genericCaseTypes?.length ?
                        props.customer.genericCaseTypes.map(p => p.name).join(", ") :
                        ""
                    }
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            {props.customer.products && props.customer.products.includes(CaseType.PHYSIOTHERAPY_REFERRAL) &&
                <Detail
                    label="Physiotherapy"
                    value={props.customer.physioOrigins && props.customer.physioOrigins.length ?
                        props.customer.physioOrigins.map(p => PHYSIOTHERAPY_ORIGINS.find(x => x.value === p)?.displayName).join(", ") :
                        "None"}
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            {props.customer.products && props.customer.products.includes(CaseType.COVID_19) &&
                <Detail
                    label="COVID-19 Features"
                    value={props.customer.covid19Features && props.customer.covid19Features.length ?
                        props.customer.covid19Features.map(p => COVID_19_FEATURES.find(x => x.value === p)?.displayName).join(", ") :
                        "None"}
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            {props.customer.products && props.customer.products.includes(CaseType.SRA) &&
                <Detail
                    label="Assessment and Well-being"
                    value={props.customer.assessmentAndWellBeingFeatures && props.customer.assessmentAndWellBeingFeatures.length ?
                        props.customer.assessmentAndWellBeingFeatures
                            .map(p => ASSESSMENT_AND_WELL_BEING_FEATURES.find(x => x.value === p)?.displayName).join(", ") :
                        "None"}
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            { ppqAutomationFeatureEnabled && props.customer.products && props.customer.products.includes(CaseType.PPQ) &&
                <Detail
                    label="Automated PPQ"
                    value={getYesNoOptionEnabledDisplayValue(props.customer.ppqAutomationEnabled)}
                    labelWidth={5}
                    valueWidth={11}
                />
            }
            <Detail label="Customer Notes" value={getDisplayValue(props.customer.customerNotes)} labelWidth={5} valueWidth={11} />
            <Detail
                label="Default Customer Type"
                value={getYesNoOptionEnabledDisplayValue(props.customer.isDefaultCustomerType)}
                labelWidth={5}
                valueWidth={11}
            />
            {props.customer.isDefaultCustomerType === false &&
                <Detail label="Customer Type" value={getDisplayValue(props.customer.customerType)} labelWidth={5} valueWidth={11} />
            }
            <Detail label="Active" value={JSON.stringify(props.customer.active)} labelWidth={5} valueWidth={11} />
            <Detail label="Use Toyota Templates" value={props.customer?.useToyotaTemplates ? "Yes" : "No"} labelWidth={5} valueWidth={11} />
            {customerLevelPartialMatchChanges && <Detail
                label="Use Org Level 1 in Match Criteria"
                value={props.customer?.useOrgLevel1InMatchCriteria ? "Yes" : "No"}
                labelWidth={5}
                valueWidth={11}
            />}

            <h3>MULTI FACTOR AUTHENTICATION (MFA)</h3>
            <Detail
                label="Customer Users"
                value={getYesNoOptionEnabledDisplayValue(props.customer.isMultiFactorAuthenticationEnabled)}
                labelWidth={5}
                valueWidth={11}
            />
            <Detail
                label="Employees"
                value={getYesNoOptionEnabledDisplayValue(props.customer.isEmployeeUserMultiFactorAuthenticationEnabled)}
                labelWidth={5}
                valueWidth={11}
            />

            <h3>FINANCE INFORMATION</h3>
            <Detail label="OHD Payment Method" value={getDisplayValue(props.customer.ohdPaymentMethod)} labelWidth={5} valueWidth={11} />
            <Detail label="Finance Customer Name" value={getDisplayValue(props.customer.financeCustomerName)} labelWidth={5} valueWidth={11} />
            <Detail label="Finance Customer Reference" value={getDisplayValue(props.customer.accountCode)} labelWidth={5} valueWidth={11} />
            <Detail label="Finance Cost Centre" value={getDisplayValue(props.customer.contractCode)} labelWidth={5} valueWidth={11} />
            <Detail label="Invoicing Method" value={getDisplayValue(props.customer.invoicingMethod)} labelWidth={5} valueWidth={11} />
            {props.customer.priceBucket && <Detail label="Price Bucket" value={props.customer.priceBucket.toFixed(0)} labelWidth={5} valueWidth={11} />}
            {enableSNCAndFinanceInfoFunctionality &&
                <Detail label="SNC Rules" value={getDisplayValue(props.customer.sncRule?.name)} labelWidth={5} valueWidth={11} />
            }
            <Detail label="Customer Purchase Order Number" value={getDisplayValue(props.customer.purchaseOrderNumber)} labelWidth={5} valueWidth={11} />
            <Detail
                label="Allowance Start-Date"
                value={props?.customer?.allowanceStartDate?.isValid() ? props.customer.allowanceStartDate.format("DD/MM/YYYY").toString() : "DD/MM/YYYY"}
                labelWidth={5}
                valueWidth={11}
            />
            <Detail
                label="Allowance End-Date"
                value={props.customer?.allowanceEndDate?.isValid() ? props.customer.allowanceEndDate.format("DD/MM/YYYY").toString() : "DD/MM/YYYY"}
                labelWidth={5}
                valueWidth={11}
            />
            <Detail label="Org level 1 billing" value={props.customer?.orgLevel1Billing ? "Yes" : "No"} labelWidth={5} valueWidth={11} />
            <Detail
                label="Purchase Order required per Case"
                value={props.customer?.purchaseOrderRequiredPerCase ? "Yes" : "No"}
                labelWidth={5}
                valueWidth={11} />
        </Grid>
    );
};

const CoreDetailsWithActions: React.FC<DetailProps> = (props) => {
    return (
        <div>
            <CoreDetails customer={props.customer} />
            <Authorize roles={[ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.SYSTEM_ADMIN]}>
                <Divider />
                <Segment basic clearing vertical>
                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                        <DeleteButton />
                    </Authorize>
                    <CoreDetailsButtons customer={props.customer} />
                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                        <Button icon="pencil" content="Edit" floated="right" as={Link} href={`${props.path}/edit`} />
                    </Authorize>
                </Segment>
            </Authorize>
        </div>
    );
};

export const coreDetailsTab = (props: DetailProps) => ({
    path: "",
    menuItem: "Details",
    render: () => <Tab.Pane><CoreDetailsWithActions {...props} /></Tab.Pane>
});
